import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Activity,
  Users,
  UserX,
  TrendingUp,
  UserCheck,
  Zap,
  Download,
  AlertTriangle,
} from "lucide-react";
import {
  useUserActivityMetrics,
  useTopActiveUsers,
  useDormantUsers,
} from "@/hooks/useUserActivityMetrics";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

interface Props {
  startDate: string;
  endDate: string;
}

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  try {
    return format(new Date(iso), "dd/MM/yyyy HH:mm", { locale: ptBR });
  } catch {
    return "—";
  }
}

function planBadge(plan: string) {
  if (plan === "premium" || plan === "premium_yearly")
    return <Badge className="bg-amber-500/15 text-amber-700 border-amber-500/30">PRO</Badge>;
  if (plan === "trial")
    return <Badge className="bg-blue-500/15 text-blue-700 border-blue-500/30">Trial</Badge>;
  return <Badge variant="outline">Free</Badge>;
}

function downloadCSV(rows: Record<string, unknown>[], filename: string) {
  if (rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const escape = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function UserActivityPanel({ startDate, endDate }: Props) {
  const [dormantDays, setDormantDays] = useState(30);
  const { data: metrics, isLoading } = useUserActivityMetrics(startDate, endDate);
  const { data: topUsers, isLoading: loadingTop } = useTopActiveUsers(startDate, endDate);
  const { data: dormant, isLoading: loadingDormant } = useDormantUsers(dormantDays);

  if (isLoading || !metrics) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  const kpis = [
    { icon: Activity, label: "Ativos hoje (DAU)", value: metrics.dau, color: "text-emerald-600 bg-emerald-500/15" },
    { icon: Users, label: "Ativos 7 dias (WAU)", value: metrics.wau, color: "text-blue-600 bg-blue-500/15" },
    { icon: Users, label: "Ativos 30 dias (MAU)", value: metrics.mau, color: "text-purple-600 bg-purple-500/15" },
    { icon: Zap, label: "Stickiness (DAU/MAU)", value: `${metrics.stickiness}%`, color: "text-amber-600 bg-amber-500/15" },
    { icon: UserCheck, label: "Retenção novos usuários", value: `${metrics.retention.rate}%`, sub: `${metrics.retention.returning}/${metrics.retention.new_signups} voltaram`, color: "text-cyan-600 bg-cyan-500/15" },
    { icon: UserX, label: "Nunca voltaram", value: metrics.retention.never_returned, sub: "cadastros do período", color: "text-rose-600 bg-rose-500/15" },
    { icon: AlertTriangle, label: "Churn de atividade", value: metrics.churned, sub: "ativos que sumiram há 14+ dias", color: "text-orange-600 bg-orange-500/15" },
    { icon: TrendingUp, label: "Novos cadastros", value: metrics.retention.new_signups, sub: "no período", color: "text-pink-600 bg-pink-500/15" },
  ];

  const frequencyData = [
    { faixa: "1 dia", usuarios: metrics.frequency["1"] ?? 0 },
    { faixa: "2–5 dias", usuarios: metrics.frequency["2-5"] ?? 0 },
    { faixa: "6–15 dias", usuarios: metrics.frequency["6-15"] ?? 0 },
    { faixa: "16+ dias", usuarios: metrics.frequency["16+"] ?? 0 },
  ];

  const dailyData = (metrics.daily ?? []).map((d) => ({
    date: format(parseISO(d.day), "dd/MM"),
    ativos: d.active_users,
  }));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-1">Atividade de Usuários</h2>
        <p className="text-muted-foreground text-sm">
          Frequência de acesso, retenção, dormentes e ranking de engajamento.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {kpis.map((k) => (
          <Card key={k.label}>
            <CardContent className="p-4 flex items-start gap-3">
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${k.color}`}>
                <k.icon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="text-2xl font-bold leading-tight">{k.value}</div>
                <div className="text-sm font-medium text-foreground">{k.label}</div>
                {k.sub && <div className="text-xs text-muted-foreground mt-0.5">{k.sub}</div>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Gráficos */}
      <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Usuários ativos por dia</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Line type="monotone" dataKey="ativos" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Distribuição por faixa de acessos</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={frequencyData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="faixa" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="usuarios" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Top ativos */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Top 20 usuários mais ativos no período</CardTitle>
          {topUsers && topUsers.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                downloadCSV(
                  topUsers.map((u) => ({
                    nome: u.full_name ?? "",
                    email: u.email ?? "",
                    plano: u.plan_type,
                    dias_ativos: u.days_active,
                    total_acessos: u.total_hits,
                    ultimo_acesso: u.last_activity_at ?? "",
                  })),
                  `top-ativos-${startDate}-a-${endDate}.csv`
                )
              }
            >
              <Download className="h-4 w-4 mr-2" /> CSV
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">Usuário</th>
                  <th className="px-4 py-2 text-left font-semibold">Plano</th>
                  <th className="px-4 py-2 text-right font-semibold">Dias ativos</th>
                  <th className="px-4 py-2 text-right font-semibold">Acessos</th>
                  <th className="px-4 py-2 text-left font-semibold">Último acesso</th>
                </tr>
              </thead>
              <tbody>
                {loadingTop ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Carregando…</td></tr>
                ) : !topUsers || topUsers.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Nenhuma atividade no período.</td></tr>
                ) : (
                  topUsers.map((u) => (
                    <tr key={u.user_id} className="border-t border-border">
                      <td className="px-4 py-2">
                        <div className="font-medium">{u.full_name || "Sem nome"}</div>
                        <div className="text-xs text-muted-foreground">{u.email || u.user_id.slice(0, 8)}</div>
                      </td>
                      <td className="px-4 py-2">{planBadge(u.plan_type)}</td>
                      <td className="px-4 py-2 text-right font-semibold">{u.days_active}</td>
                      <td className="px-4 py-2 text-right">{u.total_hits}</td>
                      <td className="px-4 py-2 text-muted-foreground">{fmtDate(u.last_activity_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Dormentes */}
      <Card>
        <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <CardTitle className="text-base">Usuários que não voltam mais</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Candidatos sem atividade há {dormantDays}+ dias (limite 500 registros).
            </p>
          </div>
          <div className="flex items-center gap-2">
            {[30, 60, 90].map((d) => (
              <Button
                key={d}
                size="sm"
                variant={dormantDays === d ? "default" : "outline"}
                onClick={() => setDormantDays(d)}
              >
                {d}d
              </Button>
            ))}
            {dormant && dormant.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  downloadCSV(
                    dormant.map((u) => ({
                      nome: u.full_name ?? "",
                      email: u.email ?? "",
                      plano: u.plan_type,
                      cadastro: u.created_at,
                      ultimo_acesso: u.last_activity_at ?? "",
                      dias_inativo: u.days_inactive,
                    })),
                    `dormentes-${dormantDays}d.csv`
                  )
                }
              >
                <Download className="h-4 w-4 mr-2" /> CSV
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">Usuário</th>
                  <th className="px-4 py-2 text-left font-semibold">Plano</th>
                  <th className="px-4 py-2 text-left font-semibold">Cadastro</th>
                  <th className="px-4 py-2 text-left font-semibold">Último acesso</th>
                  <th className="px-4 py-2 text-right font-semibold">Dias inativo</th>
                </tr>
              </thead>
              <tbody>
                {loadingDormant ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Carregando…</td></tr>
                ) : !dormant || dormant.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Nenhum dormente encontrado.</td></tr>
                ) : (
                  dormant.map((u) => (
                    <tr key={u.user_id} className="border-t border-border">
                      <td className="px-4 py-2">
                        <div className="font-medium">{u.full_name || "Sem nome"}</div>
                        <div className="text-xs text-muted-foreground">{u.email || u.user_id.slice(0, 8)}</div>
                      </td>
                      <td className="px-4 py-2">{planBadge(u.plan_type)}</td>
                      <td className="px-4 py-2 text-muted-foreground">{fmtDate(u.created_at)}</td>
                      <td className="px-4 py-2 text-muted-foreground">{fmtDate(u.last_activity_at)}</td>
                      <td className="px-4 py-2 text-right font-semibold text-rose-600">{u.days_inactive}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
